import { driver, type Alignment, type DriveStep, type Driver, type Side } from "driver.js";
import "driver.js/dist/driver.css";
import "./guides.css";
import { enhanceGuidePopover } from "./popoverEnhancements";
import { guideSelector } from "./selectors";
import type { GuidePlacement, PageGuideStep } from "./types";

/** Below welcome/home modals (z-50); above normal page content. */
export const PAGE_GUIDE_Z_INDEX = 40;

/** Above Bryntum editor/dialog layers (`--swivu-bryntum-z-index`). */
export const MODAL_GUIDE_Z_INDEX = 140025;

const DEFAULT_PLACEMENT: GuidePlacement = { side: "bottom", align: "start" };

function toDriveSteps(steps: PageGuideStep[]): DriveStep[] {
  return steps
    .filter((step) => document.querySelector(guideSelector(step.target)))
    .map((step, index) => {
      const placement = step.placement ?? DEFAULT_PLACEMENT;

      return {
        element: guideSelector(step.target),
        popover: {
          title: step.title,
          description: step.body,
          side: placement.side as Side,
          align: placement.align as Alignment,
          disableButtons: index === 0 ? ["previous"] : [],
        },
      };
    });
}

interface StartSwivuGuideOptions {
  steps: PageGuideStep[];
  onComplete: () => void;
  onDismiss: () => void;
  zIndex?: number;
}

export function startSwivuGuide({ steps, onComplete, onDismiss, zIndex = PAGE_GUIDE_Z_INDEX }: StartSwivuGuideOptions): Driver | null {
  const driveSteps = toDriveSteps(steps);
  if (driveSteps.length === 0) return null;

  const driverObj = driver({
    animate: true,
    smoothScroll: true,
    allowClose: true,
    overlayOpacity: 0.65,
    overlayClickBehavior: "close",
    stagePadding: 6,
    stageRadius: 10,
    popoverOffset: 6,
    showProgress: true,
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Got it",
    popoverClass: "swivu-page-guide-popover",
    overlayColor: "#000000",
    onPopoverRender: enhanceGuidePopover,
    onDestroyed: (_element, _step, { config, state }) => {
      const lastIndex = Math.max(0, (config.steps?.length ?? 1) - 1);
      if ((state.activeIndex ?? 0) >= lastIndex) onComplete();
      else onDismiss();
    },
    steps: driveSteps,
  });

  document.documentElement.style.setProperty("--swivu-page-guide-z-index", String(zIndex));
  document.documentElement.style.setProperty("--driver-z-index", String(zIndex));

  driverObj.drive();
  return driverObj;
}
